import { useFloatingAIChat } from '@cobliteam/ai/src/store';
import { Analytics } from '@cobliteam/analytics';
import useCrm from '@cobliteam/hooks/src/useEntity/useCrm';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import { CalendarClock, Sparkles } from 'lucide-react';

interface InstallationStepBodyProps {
  fleetId?: string;
  /** Closes the guided modal when launching the AI scheduling agent (a separate floating chat). */
  onLaunch?: () => void;
}

/**
 * Devices/installation step: physical install can't be done inline, so we list pending scheduling
 * tickets and let the user kick off the AI scheduling agent for each one.
 */
const InstallationStepBody = ({ fleetId, onLaunch }: InstallationStepBodyProps) => {
  const { t } = useTranslation('basic-value-onboarding');
  const { data, isLoading } = useCrm().getSchedulingTickets();
  const openSchedulingAgent = useFloatingAIChat((state) => state.open);
  const tickets = data?.tickets ?? [];

  const handleSchedule = (ticketId?: string) => {
    Analytics.track('BasicValueModalScheduleWithAiClicked', { fleetId, ticketId });
    onLaunch?.();
    openSchedulingAgent(ticketId ? t('inline.installation_specific_prompt', { ticketId }) : t('inline.installation_generic_prompt'));
  };

  // While the query is in flight, show nothing rather than the title over an empty list.
  if (isLoading) return null;

  if (tickets.length === 0) {
    return (
      <div className='flex flex-col items-start gap-3'>
        <Typography variant='text-sm' className='text-gray-11'>
          {t('inline.installation_empty')}
        </Typography>
        <Button size='sm' variant='outlined' startIcon={<Sparkles size={14} />} onClick={() => handleSchedule()}>
          {t('inline.talk_to_assistant')}
        </Button>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-2'>
      <Typography variant='text-sm' className='font-medium text-gray-11'>
        {t('inline.installation_pending')}
      </Typography>
      <div className='flex max-h-64 flex-col gap-2 overflow-y-auto'>
        {tickets.map((ticket) => {
          const plate = ticket.installationInformation?.vehicleInformation?.plack;
          return (
            <div key={ticket.id} className='flex items-center justify-between gap-3 rounded-lg border border-gray-6 border-solid p-3'>
              <span className='flex min-w-0 items-center gap-2'>
                <CalendarClock size={16} className='shrink-0 text-gray-10' aria-hidden />
                <Typography variant='text-sm' className='truncate font-medium'>
                  {plate || `#${ticket.id}`}
                </Typography>
              </span>
              <Button size='sm' variant='outlined' startIcon={<Sparkles size={14} />} onClick={() => handleSchedule(ticket.id)}>
                {t('inline.schedule_with_ai')}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InstallationStepBody;
